import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useMatches } from "react-router-dom";
import { useDocumentTitle } from "../../lib/hooks";

export interface RouteHandle {
  title: string;
  /** Parent page for nested screens: breadcrumb on desktop, back button on mobile. */
  parent?: { label: string; to: string };
  /** Page-level primary action shown in the top bar (desktop). */
  primary?: "new-schedule";
}

export interface PageMeta {
  title?: string;
  parent?: { label: string; to: string };
}

const Ctx = createContext<{ meta: PageMeta; setMeta: (m: PageMeta) => void } | null>(null);

export function PageMetaProvider({ children }: { children: ReactNode }) {
  const [meta, setMeta] = useState<PageMeta>({});
  return <Ctx.Provider value={{ meta, setMeta }}>{children}</Ctx.Provider>;
}

/** Let a page override the title/parent from its route handle (e.g. a summary's topic). */
export function usePageMeta(meta: PageMeta) {
  const ctx = useContext(Ctx);
  const key = JSON.stringify(meta);
  useEffect(() => {
    if (!ctx) return;
    ctx.setMeta(meta);
    return () => ctx.setMeta({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

/** Resolved meta for the current route. */
export function useResolvedMeta(): RouteHandle {
  const ctx = useContext(Ctx);
  const matches = useMatches();
  const handle = [...matches].reverse().find((m) => (m.handle as RouteHandle | undefined)?.title)?.handle as RouteHandle | undefined;
  const resolved: RouteHandle = {
    title: ctx?.meta.title ?? handle?.title ?? "LOVE CPMD",
    parent: ctx?.meta.parent ?? handle?.parent,
    primary: handle?.primary,
  };
  useDocumentTitle(resolved.title);
  return resolved;
}
